/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */
import './style/templateSelect.scss';
import {Link, Loader, WithBackground} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import React, {MouseEventHandler, useEffect, useMemo, useRef} from 'react';

import {useTranslation} from 'react-i18next';

import DefaultEmail from '../../../models/defaultEmailModel';

import EmailTemplate from '../../../models/emailTemplateModel';

import {EmailFlow} from '../../../util/interfaces';

import {BluemateButton, BluemateTabs} from '../../controls';

import InfoBanner from '../../misc/infoBanner';

import {TemplateTab} from './templateSelectController';

interface Props {
    onClose: MouseEventHandler;
    onChooseDefault: (defaultEmail?: DefaultEmail) => MouseEventHandler;
    onChooseTemplate: (template: EmailTemplate) => MouseEventHandler;
    onChangeTab: (tab: TemplateTab) => void;
    onOpenTemplate: (template: EmailTemplate) => MouseEventHandler;
    onPreviewTemplate: (body: string, isHTML?: boolean, isLegacy?: boolean, bcc?: string, subject?: string) => void;
    onDeleteTemplate: (template: EmailTemplate) => MouseEventHandler;
    onToggleShowAll: MouseEventHandler;
    onDuplicateTemplate: (template: EmailTemplate) => MouseEventHandler;
    templates: EmailTemplate[];
    loading: boolean;
    active: boolean;
    activeTab: TemplateTab;
    currentTemplateId: string | null;
    flow: EmailFlow;
    showAll: boolean;
    defaultEmails: Array<DefaultEmail>;
}

export default function TemplateSelect(props: Props) {
    const {t} = useTranslation();
    const {
        active,
        templates,
        onOpenTemplate,
        onChooseTemplate,
        onPreviewTemplate,
        onChooseDefault,
        onChangeTab,
        onClose,
        loading,
        activeTab,
        flow,
        currentTemplateId,
        onDeleteTemplate,
        showAll,
        onToggleShowAll,
        onDuplicateTemplate,
        defaultEmails
    } = props;
    const showDefaultEmails: Array<DefaultEmail> = useMemo(() => {
        if (showAll) return defaultEmails;
        return defaultEmails.filter((defaultEmail: DefaultEmail) => defaultEmail.emailFlows.includes(flow));
    }, [showAll, defaultEmails, flow]);
    const currentTemplate = useRef<HTMLDivElement | null>(null);

    function onShowDefaultPreview(defaultEmail: DefaultEmail) {
        return function () {
            if (!defaultEmail.previewURL) return;
            onPreviewTemplate(defaultEmail.previewURL);
        };
    }

    function onShowPreviewCustom(template: EmailTemplate) {
        return function () {
            let body: string,
                isLegacy: boolean = false;
            if (template.htmlBody.length) body = template.htmlBody;
            else {
                body = template.body;
                isLegacy = true;
            }
            onPreviewTemplate(body, true, isLegacy, template.bcc, template.subject);
        };
    }

    function scrollInView() {
        currentTemplate.current?.scrollIntoView({behavior: 'smooth'});
    }

    const templateTabs = [
        {key: TemplateTab.DEFAULT, label: t('email.templateSelect.tab.default', 'Template Layouts')},
        {key: TemplateTab.CUSTOM, label: t('email.templateSelect.tab.custom', 'Opgeslagen Templates')}
    ];

    function renderButton(label: string, onClick: MouseEventHandler, primary: boolean = true) {
        return (
            <div className={classNames('template-button', primary && 'primary')} onClick={onClick}>
                <label className='button-label'>{label}</label>
            </div>
        );
    }

    function renderDefaultTab() {
        return (
            <div className='tab default'>
                <div className='labels'>
                    <label>
                        {t(
                            'email.templateSelect.description',
                            'Je kunt een template kiezen, aanpassen en voorvertonen.'
                        )}
                    </label>
                    <InfoBanner variant='success' persistDismiss={false}>
                        <label>
                            {t(
                                'email.templateSelect.conversionTip',
                                "Conversietip: Voeg persoonlijke foto's toe om je te benadrukken als kleine ondernemer; dit vergroot de kans op reviews."
                            )}{' '}
                            <Link route='https://bluemate.nl/dit-zijn-de-best-presterende-e-mail-campagnes-voor-review-op-bol-com-tips/'>
                                {t('email.templateSelect.readMore', 'Lees meer')}
                            </Link>
                        </label>
                    </InfoBanner>
                </div>
                <div className='templates'>
                    <Loader active={loading} />
                    {showDefaultEmails.map(renderDefaultEmail)}
                    <div className='empty-template' onClick={onChooseDefault()}>
                        <label className='empty-label'>{t('email.templateSelect.startEmpty', 'Begin leeg')}</label>
                    </div>
                </div>
            </div>
        );
    }

    function renderDefaultEmail(defaultEmail: DefaultEmail, index: number) {
        return (
            <div key={index} className='template'>
                <div className='preview-container'>
                    <img src={defaultEmail.previewURL ?? ''} alt={defaultEmail.templateName} className='preview' />
                </div>
                <label className='template-name'>{defaultEmail.templateName}</label>
                <div className='options-overlay'>
                    {renderButton(
                        t('email.templateSelect.chooseEdit', 'Kiezen & Aanpassen'),
                        onChooseDefault(defaultEmail),
                        true
                    )}
                    {renderButton(
                        t('email.templateSelect.preview', 'Preview'),
                        onShowDefaultPreview(defaultEmail),
                        false
                    )}
                </div>
            </div>
        );
    }

    function renderCustomTab() {
        return (
            <div className='tab custom'>
                <InfoBanner variant='warning' persistDismiss={false}>
                    {t(
                        'email.templateSelect.sharedBanner',
                        'Let op: Wijzigingen aan een opgeslagen template gelden voor alle e-mailflows die deze template gebruiken. Wil je dit niet, dupliceer dan het template of maak een nieuwe.'
                    )}
                </InfoBanner>
                <div className='toolbar'>
                    <BluemateButton
                        onClick={() => onChangeTab(TemplateTab.DEFAULT)}
                        label={t('email.templateSelect.newTemplate', '+ Nieuwe E-mail Template maken')}
                    />
                </div>
                <div className='row head'>
                    <label>{t('email.templateSelect.emailTitle', 'EMAIL TITEL')}</label>
                    <label>{t('email.templateSelect.lastUpdated', 'LAATST BIJGEWERKT')}</label>
                </div>
                <div className='templates-container'>
                    <div className='templates'>
                        <Loader active={loading} />
                        {templates.map(renderCustomTemplate)}
                    </div>
                </div>
            </div>
        );
    }

    function renderCustomTemplate(template: EmailTemplate, index: number) {
        const isSelected = currentTemplateId === template.id;
        return (
            <div
                className={classNames('row', isSelected && 'current')}
                key={index}
                tabIndex={-1}
                ref={isSelected ? currentTemplate : undefined}
            >
                <label>
                    {template.name}
                    {isSelected && (
                        <>
                            &nbsp;
                            <span className='current-label'>{t('email.templateSelect.currentSuffix', '(huidig)')}</span>
                        </>
                    )}
                </label>
                <label>{template.updatedAtMoment?.format('DD-MM-YYYY HH:mm:ss') ?? '-'}</label>
                <div className='options-container'>
                    {!isSelected && (
                        <label className='option' onClick={onChooseTemplate(template)}>
                            {t('email.templateSelect.choose', 'Kiezen')}
                        </label>
                    )}
                    <label className='option' onClick={onShowPreviewCustom(template)}>
                        {t('email.templateSelect.view', 'Bekijken')}
                    </label>
                    <label className='option' onClick={onOpenTemplate(template)}>
                        {t('email.templateSelect.edit', 'Aanpassen')}
                    </label>
                    <label className='option' onClick={onDuplicateTemplate(template)}>
                        {t('email.templateSelect.copy', 'Kopiëren')}
                    </label>
                    {!isSelected && (
                        <label className='option red' onClick={onDeleteTemplate(template)}>
                            {t('email.templateSelect.delete', 'Verwijderen')}
                        </label>
                    )}
                </div>
            </div>
        );
    }

    function renderAllToggle() {
        if (activeTab !== TemplateTab.DEFAULT) return null;
        const text = showAll
            ? t('email.templateSelect.showSpecific', 'Specifieke templates tonen')
            : t('email.templateSelect.showAll', 'Alle templates tonen');
        return (
            <Link className='all-toggle' onClick={onToggleShowAll} underline>
                {text}
            </Link>
        );
    }

    useEffect(() => {
        if (active && activeTab === TemplateTab.CUSTOM && templates.length) window.setTimeout(scrollInView);
    }, [activeTab, active, templates]);

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='template-select default-popup'>
                <div className='header-container'>
                    <label className='header-title'>
                        {t('email.templateSelect.title', 'Kies jouw E-mail Template')}
                    </label>
                </div>
                <div className='body-container'>
                    <BluemateTabs<TemplateTab>
                        tabs={templateTabs}
                        activeTab={activeTab}
                        onChangeTab={onChangeTab}
                        className='tabs-container'
                    />
                    {activeTab === TemplateTab.DEFAULT ? renderDefaultTab() : renderCustomTab()}
                    {renderAllToggle()}
                </div>
            </div>
        </WithBackground>
    );
}
